import classNames from "classnames";
import React, { useState } from "react";
import { MdAssignmentTurnedIn } from "react-icons/md";
import { toast } from "react-toastify";

import { SettingsIconButton } from "../../../../components/button/SettingsIconButton";
import { SettingsCard } from "../../../../components/card/SettingsCard";
import { SettingsDropdown } from "../../../../components/dropdown/SettingsDropdown";
import { SettingsConfirmModal } from "../../../../components/modal/SettingsConfirmModal";
import { DotsSvg } from "../../../../components/svg/DotsSvg";
import { EditPencilSvg } from "../../../../components/svg/EditPencilSvg";
import PlusSvg from "../../../../components/svg/PlusSvg";
import { TrashSvg } from "../../../../components/svg/TrashSvg";
import { StatusName } from "../../../../factories/statuses/enums/status.enum";
import { SubStatusModel } from "../../../../factories/statuses/models/status.model";
import {
  useDeleteSubStatusMutation,
  useLazyGetSubStatusUsageQuery,
} from "../../../../slices/StatusesSlice";
import { serverErrorMessage } from "../../workTypesV2/model/workTypeEditor.utils";
import { StatusCardProps } from "../model/statuses.types";
import {
  formatWorkTypeUsage,
  getSubStatusAssignedWorkTypeIds,
  isEmptyWorkTypeUsage,
  STATUS_COLORS,
  subStatusDeleteText,
} from "../model/statuses.utils";
import { AssignWorkTypesPanel } from "./AssignWorkTypesPanel";
import { StatusForm } from "./StatusForm";

export const StatusCard: React.FC<StatusCardProps> = ({
  status,
  workTypes,
  assignmentDrafts,
  isSavingAssignment,
  onRefresh,
  onSaveAssignment,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editSubStatus, setEditSubStatus] = useState<SubStatusModel | null>(
    null,
  );
  const [subStatusToDelete, setSubStatusToDelete] =
    useState<SubStatusModel | null>(null);
  const [assigningSubStatusId, setAssigningSubStatusId] = useState<
    string | null
  >(null);

  const [deleteSubStatus, { isLoading: isLoadingDelete }] =
    useDeleteSubStatusMutation();
  // QA batch 16 (SET-014): before deleting, ask the server how many work
  // items use the status; they are moved to the picked status first.
  const [loadUsage, { data: usageData, isFetching: isLoadingUsage }] =
    useLazyGetSubStatusUsageQuery();
  const usage =
    usageData && subStatusToDelete && usageData.id === subStatusToDelete.id
      ? usageData
      : undefined;
  const [moveTo, setMoveTo] = useState("group");
  const openDelete = (item: SubStatusModel) => {
    setSubStatusToDelete(item);
    setMoveTo("group");
    void loadUsage(item.id);
  };

  if (status.name === StatusName.trial) {
    return null;
  }

  const colors = STATUS_COLORS[status.name];
  const statusUsageCount = status.inServiceCounts;

  const handleDeleteSubStatus = async () => {
    if (!subStatusToDelete) {
      return;
    }

    try {
      await deleteSubStatus({
        ...subStatusToDelete,
        moveTo: usage?.workCount ? moveTo : undefined,
      }).unwrap();
      toast("Status successfully Deleted", {
        type: "success",
      });
      setSubStatusToDelete(null);
      await onRefresh();
    } catch (e) {
      toast(serverErrorMessage(e, "Something went wrong"), {
        type: "error",
      });
    }
  };

  return (
    <>
      <SettingsCard>
        <div className={"flex items-start justify-between gap-2 p-4"}>
          <div className={"min-w-0"}>
            <div className={"flex items-center gap-1.5"}>
              <div
                className={classNames(
                  "flex h-6 w-6 items-center justify-center rounded-full",
                  colors.dotShadow,
                )}>
                <div
                  className={classNames("h-3 w-3 rounded-full", colors.dot)}
                />
              </div>
              <div
                className={classNames(
                  "truncate text-lg font-semibold",
                  colors.text,
                )}>
                {status.name}
              </div>
            </div>
            <div
              className={classNames(
                "mt-1 text-xs font-medium",
                isEmptyWorkTypeUsage(statusUsageCount)
                  ? "text-[#E5484D]"
                  : "text-[#667085]",
              )}>
              {formatWorkTypeUsage(statusUsageCount)}
            </div>
          </div>
        </div>

        <div className={"mb-4 px-4"}>
          <button
            type={"button"}
            onClick={() => setIsAdding(true)}
            className={
              "transitions-all flex h-10 w-full items-center justify-center gap-2 rounded-lg border border-[#C8D2DE] bg-white px-4 text-sm font-semibold text-[#253747] duration-300 hover:border-[#798CA4] hover:bg-[#4C637B0D]"
            }>
            <PlusSvg />
            <span>Add status</span>
          </button>
        </div>

        <div className={"transition-all duration-300"}>
          {isAdding && (
            <StatusForm
              onClose={() => setIsAdding(false)}
              workTypeStatusId={status.id}
              onSuccess={onRefresh}
            />
          )}
          <div>
            {(status?.workTypeSubStatuses
              ? [...status.workTypeSubStatuses].sort((a, b) =>
                  a.subStatus.localeCompare(b.subStatus),
                )
              : []
            ).map((item) => (
              <React.Fragment key={item.id}>
                {editSubStatus && editSubStatus.id === item.id ? (
                  <StatusForm
                    onClose={() => setEditSubStatus(null)}
                    subStatus={editSubStatus}
                    onSuccess={onRefresh}
                  />
                ) : (
                  (() => {
                    const subStatusUsageCount =
                      assignmentDrafts[item.id]?.length ?? item.inServiceCounts;

                    return (
                      <div
                        className={
                          "group border-b border-[#E4E9F2] last:border-none"
                        }>
                        <div
                          className={
                            "min-h-12 grid grid-cols-[1fr_max-content] items-center pl-4 pr-1"
                          }>
                          <div className={"min-w-0 py-2"}>
                            <div
                              title={item.subStatus}
                              className={
                                "truncate text-base font-medium text-[#3F5266]"
                              }>
                              {item.subStatus}
                            </div>
                            <div
                              className={classNames(
                                "mt-0.5 text-xs font-medium",
                                isEmptyWorkTypeUsage(subStatusUsageCount)
                                  ? "text-[#E5484D]"
                                  : "text-[#8A97A8]",
                              )}>
                              {formatWorkTypeUsage(subStatusUsageCount)}
                            </div>
                          </div>
                          <div
                            className={classNames(
                              assigningSubStatusId === item.id
                                ? "pointer-events-auto opacity-100"
                                : "pointer-events-none opacity-0 transition-opacity duration-100 group-hover:pointer-events-auto group-hover:opacity-100",
                            )}>
                            <SettingsDropdown
                              actionsList={[
                                {
                                  icon: <MdAssignmentTurnedIn size={18} />,
                                  name: "Assign to work types",
                                  action: () =>
                                    setAssigningSubStatusId(item.id),
                                  disabled: isLoadingDelete,
                                },
                                {
                                  icon: <EditPencilSvg />,
                                  name: "Edit",
                                  action: () => setEditSubStatus(item),
                                  disabled: isLoadingDelete,
                                },
                                {
                                  icon: <TrashSvg />,
                                  name: "Delete",
                                  iconColor: "text-[#DC2626]",
                                  extraClasses: "text-[#DC2626]",
                                  action: () => openDelete(item),
                                  disabled: isLoadingDelete,
                                },
                              ]}>
                              <SettingsIconButton
                                icon={<DotsSvg />}
                                onClick={() => null}
                                extraClasses={"!h-8 !w-8"}
                              />
                            </SettingsDropdown>
                          </div>
                        </div>
                        {assigningSubStatusId === item.id && (
                          <AssignWorkTypesPanel
                            subStatus={item}
                            workTypes={workTypes}
                            selectedWorkTypeIds={
                              assignmentDrafts[item.id] ??
                              getSubStatusAssignedWorkTypeIds(item)
                            }
                            isSaving={isSavingAssignment}
                            onClose={() => setAssigningSubStatusId(null)}
                            onSave={async (subStatusId, workTypeIds) => {
                              await onSaveAssignment(subStatusId, workTypeIds);
                              setAssigningSubStatusId(null);
                            }}
                          />
                        )}
                      </div>
                    );
                  })()
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </SettingsCard>

      {subStatusToDelete && (
        <SettingsConfirmModal
          onClose={() => setSubStatusToDelete(null)}
          onConfirm={() => {
            if (!isLoadingUsage) void handleDeleteSubStatus();
          }}
          isLoading={isLoadingDelete || isLoadingUsage}
          icon={<TrashSvg />}
          title={`Delete ‘${subStatusToDelete.subStatus}’ status?`}
          description={subStatusDeleteText(usage, isLoadingUsage)}
          confirmText={usage?.workCount ? "Move and delete" : "Delete"}
          variant={"danger"}>
          {!!usage?.workCount && (
            <label className={"mt-3 block text-sm text-[#3F5266]"}>
              <span className={"mb-1 block font-semibold"}>Move them to</span>
              <select
                value={moveTo}
                onChange={(event) => setMoveTo(event.target.value)}
                className={
                  "h-10 w-full rounded-lg border border-[#C8D2DE] bg-white px-3 text-sm"
                }>
                {usage.moveTargets.map((target) => (
                  <option
                    key={target.id ?? "group"}
                    value={target.id ?? "group"}>
                    {target.id ? target.name : `${target.name} (no sub-status)`}
                  </option>
                ))}
              </select>
            </label>
          )}
        </SettingsConfirmModal>
      )}
    </>
  );
};
